import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import { useTheme } from '@mui/material/styles';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { NavItemCollapsed } from './nav-item-collapsed';
import { SubmenuPanel } from './submenu-panel';
import type { SidebarItem } from './types';
import { useSidebarData } from './use-sidebar-data';

const SIDEBAR_WIDTH_COLLAPSED = 70;

export const Sidebar = () => {
  const { sidebarData } = useSidebarData();
  const theme = useTheme();
  const location = useLocation();

  // Show submenu when current route matches the parent item's path or any child path
  const activeItemWithChildren = useMemo(
    () =>
      sidebarData
        .flatMap(section => section.items)
        .find(item => {
          if (!item.children || item.children.length === 0) {
            return false;
          }

          // Standard path matching: item path or child path
          const matchesPath =
            (item.path && location.pathname.startsWith(item.path)) ||
            item.children.some(child => child.path && location.pathname.startsWith(child.path));

          if (matchesPath) {
            return true;
          }

          // Special case for Community: match any /community-hub/spaces/{id}/community path
          if (item.id === 'community') {
            const communityRoutePattern = /\/community-hub\/spaces\/[^/]+\/community/;
            return communityRoutePattern.test(location.pathname);
          }

          return false;
        }),
    [sidebarData, location.pathname],
  );

  // Sidebar is always collapsed (icon-only)
  const isCollapsed = !activeItemWithChildren;

  const renderNavItem = (item: SidebarItem) => <NavItemCollapsed key={item.id} item={item} />;

  return (
    <Box
      sx={{
        position: 'fixed',
        borderRadius: isCollapsed ? '0 20px 0 0' : 0,
        top: '84px',
        bgcolor: '#fafafa',
        left: 0,
        height: 'calc(100vh - 96px)',
        display: 'none',
        borderRight: isCollapsed ? 0 : `1px solid ${theme.palette.grey[200]}`,
        zIndex: 1101,
        flexDirection: 'column',
        boxShadow: '14px 17px 40px 0px rgba(3, 17, 31, 0.08)',
        [theme.breakpoints.up('lg')]: {
          display: 'flex',
        },
      }}
    >
      {/* Main Sidebar */}
      <Box
        sx={{
          position: 'relative',
          width: SIDEBAR_WIDTH_COLLAPSED,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          pt: 2,
          pb: 5,
          px: 0.9,
          transition: 'width 0.2s ease-in-out, padding 0.2s ease-in-out',
          overflow: 'visible',
        }}
      >
        {/* {isCollapsed && (
          <Box
            onClick={handleCloseSubmenu}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              mb: 2,
              cursor: 'pointer',
              color: theme => theme.palette.grey[900],
              '&:hover': {
                color: 'grey.700',
              },
            }}
          >
            <GoSidebarExpand size={24} />
          </Box>
        )} */}
        {/* Navigation Sections */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            // borderRight: '1px solid',
            // borderColor: 'grey.200',
          }}
        >
          {sidebarData.map(section => (
            <Box key={section.id}>
              <List
                disablePadding
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isCollapsed ? 1.75 : 1.75,
                }}
              >
                {section.items.map(renderNavItem)}
              </List>
              {section.showDivider && (
                <Divider
                  sx={{
                    mt: 2,
                    borderColor: 'grey.300',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Social Icons */}
        {/* <SocialIcons links={socialLinks} collapsed={isCollapsed} /> */}
      </Box>
      {/* Submenu Panel */}
      {activeItemWithChildren && <SubmenuPanel item={activeItemWithChildren} />}
    </Box>
  );
};

export default Sidebar;
